import React, { createContext, useContext, useMemo, useState, type PropsWithChildren } from 'react';

type TeamsSettingsContextType = {
	teamTypesCount: number;
	setTeamTypesCount: (count: number) => void;
	defaultTeamType: string;
	setDefaultTeamType: (name: string) => void;
	editingTeamTypeName: string | undefined;
	setEditingTeamTypeName: (name: string) => void;
	editingTeamTypeId: string | undefined;
	setEditingTeamTypeId: (id: string) => void;
	editingTeamTypeIsDefault: boolean;
	setEditingTeamTypeIsDefault: (isDefault: boolean) => void;
};

const TeamsSettingsContext = createContext<TeamsSettingsContextType | undefined>(undefined);

export const TeamsSettingsProvider = ({ children }: PropsWithChildren) => {
	const [teamTypesCount, setTeamTypesCount] = useState(0);
	const [defaultTeamType, setDefaultTeamType] = useState<string>('');
	const [editingTeamTypeName, setEditingTeamTypeName] = useState<string | undefined>();
	const [editingTeamTypeId, setEditingTeamTypeId] = useState<string | undefined>();
	const [editingTeamTypeIsDefault, setEditingTeamTypeIsDefault] = useState<boolean>(true);

	const value = useMemo(
		() => ({
			teamTypesCount,
			setTeamTypesCount,
			defaultTeamType,
			setDefaultTeamType,
			editingTeamTypeName,
			setEditingTeamTypeName,
			editingTeamTypeId,
			setEditingTeamTypeId,
			editingTeamTypeIsDefault,
			setEditingTeamTypeIsDefault,
		}),
		[teamTypesCount, defaultTeamType, editingTeamTypeName, editingTeamTypeId, editingTeamTypeIsDefault],
	);

	return <TeamsSettingsContext.Provider value={value}>{children}</TeamsSettingsContext.Provider>;
};

export const useTeamsSettingsContext = () => {
	const context = useContext(TeamsSettingsContext);
	if (context === undefined) {
		throw new Error('useTeamsSettingsContext must be used within a TeamsSettingsProvider');
	}
	return context;
};
